/**
 * 授权列表的筛选状态 —— 全部存在 URL query 里。
 *
 * 规范要求「筛选条件同步到 URL（可收藏、可分享、刷新不丢）」（§6.4）。
 * 把它做成 hook 而不是组件内 useState，是因为 URL 才是唯一真相：
 * 一旦并存两份状态，「点浏览器后退但表格没变」这类 bug 就会长期存在。
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { LICENSE_STATUSES } from '../../api/types';
import type { LicenseStatus, ListLicensesQuery } from '../../api/types';
import { DEFAULT_PAGE_SIZE, PAGE_SIZES } from '../../components/Pager';
import type { PageSize } from '../../components/Pager';
import { EXPIRY_SOON_SECONDS } from '../../lib/format';

export interface LicenseFilters {
  readonly q: string;
  readonly status: LicenseStatus | undefined;
  readonly productId: string | undefined;
  /** 「7 天内到期」快捷筛选（Dashboard 卡片跳转过来时带上） */
  readonly expiringSoon: boolean;
  readonly sort: NonNullable<ListLicensesQuery['sort']>;
  readonly order: NonNullable<ListLicensesQuery['order']>;
  readonly page: number;
  readonly limit: PageSize;
}

function parseStatus(value: string | null): LicenseStatus | undefined {
  return LICENSE_STATUSES.find((s) => s === value);
}

function parseLimit(value: string | null): PageSize {
  const parsed = Number.parseInt(value ?? '', 10);
  return PAGE_SIZES.find((size) => size === parsed) ?? DEFAULT_PAGE_SIZE;
}

function parsePage(value: string | null): number {
  const parsed = Number.parseInt(value ?? '', 10);
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
}

export interface LicenseQueryApi {
  readonly filters: LicenseFilters;
  /** 传给端点层的查询对象 */
  readonly query: ListLicensesQuery;
  /** 是否存在任何生效中的筛选 —— 决定空态用哪一个组件 */
  readonly hasActiveFilters: boolean;
  readonly setFilter: (patch: Partial<LicenseFilters>) => void;
  readonly clearFilters: () => void;
}

export function useLicenseQuery(): LicenseQueryApi {
  const [params, setParams] = useSearchParams();

  const filters = useMemo<LicenseFilters>(
    () => ({
      q: params.get('q') ?? '',
      status: parseStatus(params.get('status')),
      productId: params.get('product_id') ?? undefined,
      expiringSoon: params.get('expiring') === '7d',
      sort: (params.get('sort') as LicenseFilters['sort'] | null) ?? 'updated_at',
      order: params.get('order') === 'asc' ? 'asc' : 'desc',
      page: parsePage(params.get('page')),
      limit: parseLimit(params.get('limit')),
    }),
    [params],
  );

  const setFilter = useCallback(
    (patch: Partial<LicenseFilters>) => {
      const next = new URLSearchParams(params);
      const write = (key: string, value: string | undefined) => {
        if (value === undefined || value === '') next.delete(key);
        else next.set(key, value);
      };

      if ('q' in patch) write('q', patch.q);
      if ('status' in patch) write('status', patch.status);
      if ('productId' in patch) write('product_id', patch.productId);
      if ('expiringSoon' in patch) write('expiring', patch.expiringSoon === true ? '7d' : undefined);
      if ('sort' in patch) write('sort', patch.sort);
      if ('order' in patch) write('order', patch.order);
      if ('limit' in patch) write('limit', patch.limit === undefined ? undefined : String(patch.limit));

      // 改变筛选必须回到第 1 页：否则「筛完只剩 3 条却停在第 5 页」会显示空表
      write('page', 'page' in patch && patch.page !== undefined ? String(patch.page) : undefined);

      setParams(next, { replace: false });
    },
    [params, setParams],
  );

  const clearFilters = useCallback(() => {
    setParams(new URLSearchParams(), { replace: false });
  }, [setParams]);

  const query = useMemo<ListLicensesQuery>(() => {
    const base: ListLicensesQuery = {
      sort: filters.sort,
      order: filters.order,
      page: filters.page,
      limit: filters.limit,
    };
    return {
      ...base,
      ...(filters.q === '' ? {} : { q: filters.q }),
      ...(filters.status === undefined ? {} : { status: filters.status }),
      ...(filters.productId === undefined ? {} : { product_id: filters.productId }),
      ...(filters.expiringSoon
        ? { expires_before: Math.floor(Date.now() / 1000) + EXPIRY_SOON_SECONDS }
        : {}),
    };
  }, [filters]);

  const hasActiveFilters =
    filters.q !== '' ||
    filters.status !== undefined ||
    filters.productId !== undefined ||
    filters.expiringSoon;

  return { filters, query, hasActiveFilters, setFilter, clearFilters };
}
